import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, ScrollView } from 'react-native'
import { Button, Text, Spinner, Layout, RangeDatepicker } from '@ui-kitten/components'
import { getRevisionsOnDateRange } from '../../global/Firebase/firebase'
import { UserContext } from '../../global/UserProvider'
import { useTranslation } from 'react-i18next'
import { dateRangeWithTimezoneAndAutoclose } from '../../utils/timezoneFunctions'
import { useAtomValue } from 'jotai'
import { currencyFormatterAtom, localeDateServiceAtom, storesDictAtom } from '../../global/AtomsProvider'

export default function RevisionStats({ navigation, route }) {
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const localeDateService = useAtomValue(localeDateServiceAtom)
  const db = context.db
  const [data, setData] = useState(null)
  const [store, setStore] = useState(context.user.store?.id)
  const [range, setRange] = useState({startDate: new Date(), endDate: new Date()})
  const _rangePicker = React.createRef()

  useEffect(() => {
    getDataFromFB(range)
  }, [store])

  const getDataFromFB = async (newRange) => {
    if (newRange.startDate != null && newRange.endDate != null) {
      _rangePicker.current.blur()
      setData(null)
      newRange = dateRangeWithTimezoneAndAutoclose(newRange, [store])
      const rawData = await getRevisionsOnDateRange(db, newRange, false, store)

      const categorizedData = categorizeData(rawData)
      setData(categorizedData)
    }
  }

  const categorizeData = (rawData) => {
    
    var lostItems = []
    var lostItemsTotal = 0
    var gainedItems = []
    var gainedItemsTotal = 0

    for (const [dateIndex, dateValue] of rawData.entries()) {
      for (const [revIndex, revision] of dateValue.data.entries()) {
        for (const [index, item] of revision.items.entries()) {
          let individualAmount = item.correctedIndividualAmount != null ? item.correctedIndividualAmount : item.individualAmount
          if (individualAmount < 0) {
            lostItems.push(item)
            lostItemsTotal += individualAmount
          } else {
            gainedItems.push(item)
            gainedItemsTotal += individualAmount
          }
        }
      }
    }

    lostItems.sort((a,b) => a.individualAmount - b.individualAmount)
    gainedItems.sort((a,b) => b.individualAmount - a.individualAmount)
    
    return {
      lostItems,
      lostItemsTotal, 
      gainedItems,
      gainedItemsTotal,
    }
  }

  const renderItem = ({item, index}) => {
    return (
      <ScrollView contentContainerStyle={styles.item}>
        <Text
          style={styles.itemTitle}
        >
          {item.name}
        </Text>
        <Text
          style={styles.numberInput} 
        >
          {item.inStockOld}
        </Text>
        <Text
          style={styles.numberInput} >
          {item.inStock}
        </Text>
        <Text
          style={styles.numberInput} 
        >
          {currencyFormatter.format(item.individualAmount)}
        </Text>
        <Text
          style={styles.numberInput} 
        >
          {currencyFormatter.format(item.correctedIndividualAmount || 0)}
        </Text>
        <Text
          style={styles.itemTitle}
        >
          {item.correctionReason?.text}
        </Text>
      </ScrollView>
    )
  }
  
  return (    
    <ScrollView style={{padding: 20}}>
      <View style={styles.dateFilter}>
        { Object.keys(storesDict).length > 1 && (
          <View style={styles.storeChooser}>
            { Object.entries(storesDict).map(([key, value]) => (
              <Button style={styles.storeButton} key={key} appearance='outline' status={store === key ? 'primary' : 'basic'} onPress={() => {
                setStore(key)
              }}>{value.name}</Button>
            ))}
          </View>
        )}
        <Text category='label' style={styles.label}>
          {t('filterByDate')}
        </Text>
        <RangeDatepicker
          placement='top'
          range={range}
          ref={_rangePicker}
          min={new Date('2020-12-01')}
          max={new Date('2029-12-31')}
          dateService={localeDateService}
          onSelect={(newRange) => {
            if (newRange.endDate) {
              setRange(newRange)
              getDataFromFB(newRange)
            } else if (newRange.startDate && range.startDate === newRange.startDate) {
              let endDate = new Date(newRange.startDate)
              endDate.setHours('23', '59', '59', '99')
              setRange({startDate: newRange.startDate, endDate})
              getDataFromFB({startDate: newRange.startDate, endDate})
            } else {
              setRange(newRange)
            }
          }}
        />
      </View>

      { data ? (
        <>
          <Text category='label' style={styles.header}>
            {t('revisions.missing') + ': ' + currencyFormatter.format(data.lostItemsTotal)}
          </Text>
          <Text category='label' style={styles.header}>
            {t('revisions.surplus') + ': ' + currencyFormatter.format(data.gainedItemsTotal)}
          </Text>
          <Text category='label' style={styles.header}>
            {t('revisions.balance') + ': ' + currencyFormatter.format(data.gainedItemsTotal + data.lostItemsTotal)}
          </Text>
          <Text category='label' style={styles.header}>
            {t('revisions.listMissing')}
          </Text>
          <Layout style={styles.tableView}>
            { data.lostItems.map((item, index) => (
              renderItem({item, index})
            ))}
          </Layout>

          <Text category='label' style={styles.header}>
            {t('revisions.listSurplus')}
          </Text>
          <Layout style={styles.tableView}>
            { data.gainedItems.map((item, index) => (
              renderItem({item, index})
            ))}
          </Layout>
        </>
      ) : (
        <View style={styles.spinner}>
          <Spinner size="large" />
        </View>
      )}

      <View style={{height: 100}}></View>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  header: {
    marginVertical: 10,
  },
  tableView: {
    padding: 10,
    marginBottom: 10,
    borderRadius: 10,
  },
  spinner: {
    justifyContent: 'center',
    alignItems: 'center'
  },
  storeChooser: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginVertical: 5,
    justifyContent: 'center'
  }, 
  storeButton: {
    marginLeft: 5,
  },
  item: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 2,
    paddingHorizontal: 4,
    paddingVertical: 14,
    // backgroundColor: '#FFFFFF',
    // borderRadius: 5,
  },
  itemTitle: {
    flex: 5,
    marginRight: 10,
  }, 
  numberInput: {
    flex: 2,
    marginRight: 10,
  },
  label: {
    // marginHorizontal: 20,
    marginTop: 10,
    marginBottom: 5,
  },
})